// Device detection for the print + fullscreen flows (2026-10-14, Eric).
//
// iPadOS Safari — in a tab and as a Home Screen shortcut — presents a Mac user
// agent ("Macintosh; Intel Mac OS X"). Until iPadOS 26 the iPad was told apart
// from a real Mac by navigator.maxTouchPoints > 1; iPadOS 27 no longer reports
// that, so the QC bar started treating iPads as desktops and sent them to the
// in-page print modal, which never pops AirPrint. Recognise an iPad from any
// of the signals WebKit still exposes:
//   - navigator.standalone exists on iOS/iPadOS Safari only (false in a tab,
//     true in a Home Screen app); macOS Safari does not define it at all
//   - touch events / coarse pointer (a Mac has neither)
//   - maxTouchPoints > 1 (kept for older iPadOS)
// Modern desktop Chrome reports maxTouchPoints > 0 on non-touch machines, so a
// Windows/Linux UA is never judged by touch points alone.

function uaOf(nav) { return (nav && nav.userAgent) || ''; }

export function isIpadLike(nav = typeof navigator !== 'undefined' ? navigator : null,
                           win = typeof window !== 'undefined' ? window : null) {
    const ua = uaOf(nav);
    if (/iPad/i.test(ua)) return true;
    if (!/Macintosh/i.test(ua)) return false;
    if (nav && (nav.maxTouchPoints || 0) > 1) return true;
    if (nav && 'standalone' in nav) return true;            // iOS/iPadOS-only property
    if (win && 'ontouchstart' in win) return true;
    try {
        if (win && win.matchMedia && win.matchMedia('(pointer: coarse)').matches) return true;
    } catch (_) { /* no matchMedia */ }
    return false;
}

// True only for a real desktop browser (Mac/Windows/Linux Chrome, Safari,
// Firefox, Edge). Phones, Android, and iPads (tab or Home Screen app) → false.
export function isDesktopBrowser(nav = typeof navigator !== 'undefined' ? navigator : null,
                                 win = typeof window !== 'undefined' ? window : null) {
    if (!nav || !win) return false;
    const ua = uaOf(nav);
    if (/iPhone|iPod/i.test(ua)) return false;
    if (/Android|Mobile/i.test(ua)) return false;
    if (isIpadLike(nav, win)) return false;
    return true;
}

// Home Screen web app (iOS/iPadOS "Add to Home Screen"). On iPadOS 27 that container ignores
// window.print() and window.open() entirely (verified 2026-10-14), while Safari itself prints fine.
export function isStandaloneWebApp(nav = typeof navigator !== 'undefined' ? navigator : null,
                                   win = typeof window !== 'undefined' ? window : null) {
    if (nav && nav.standalone === true) return true;
    try {
        if (win && win.matchMedia && win.matchMedia('(display-mode: standalone)').matches) return true;
    } catch (_) { /* no matchMedia */ }
    return false;
}

// Safari / WebKit major version from the UA ("Version/27.0.1 Safari/605.1.15" → 27), else 0.
export function webkitMajorVersion(nav = typeof navigator !== 'undefined' ? navigator : null) {
    const m = /Version\/(\d+)/.exec(uaOf(nav));
    return m ? parseInt(m[1], 10) : 0;
}

// iOS opens an `x-safari-https://` link in Safari proper, even from inside a Home Screen web app.
export function safariHandoffUrl(loc = typeof window !== 'undefined' ? window.location : null) {
    if (!loc) return '';
    return `x-safari-https://${loc.host}${loc.pathname}${loc.search || ''}`;
}

// The portal cannot print inside the Home Screen app on iPadOS 27+: hand off to Safari.
export function needsSafariForPrinting(nav = typeof navigator !== 'undefined' ? navigator : null,
                                       win = typeof window !== 'undefined' ? window : null) {
    return isStandaloneWebApp(nav, win) && isIpadLike(nav, win) && webkitMajorVersion(nav) >= 27;
}
